import { APICompsSchema } from "@unit/apis";

/**
 * 契約用プランマスタ（表示用・静的）。
 *
 * - 出典: 業務シート「【営事】経理関係_セールス・CS」タブ「プラン一覧」の `ダッシュボード表記` 列。
 *   デモ（unit-dashboard の demo/ContractList/src/app/data/planMaster.ts）と同じ出典。
 * - **既存の `Plan` テーブル / admin プラン API は使わない。** 契約は planId ではなく
 *   `planKey` ＋スナップショットを保持する方針で、二重管理を避けるため（6/23 確定）。
 * - `key` は永続IDなので改名・削除しない。過去キーは契約が参照し続ける。
 *
 * **`custom` は表示用にだけ持つ。** サーバ側のプランマスタは25種で `custom` を含まず、
 * `planKey: 'custom'` を送ると 400 `unknown_plan_key` になる。カスタムを選んだときは
 * `planKey` を送らず `planFreeText` を必須にすること（unit-api-spec#211 / #214）。
 * 判定には `isCustomPlan` を使う。
 */
export type ContractPlanMaster = {
  /** 永続ID（改名・削除しない） */
  key: string;
  /** ダッシュボード表記（画面で使う正式名） */
  dashboardLabel: string;
  category: APICompsSchema["PlanCategoryEnum"];
  /** 初回契約期間（月）。null = 個別設定（カスタムのみ） */
  initialTermMonths: number | null;
  /** 更新後契約期間（月）。null = 更新なし or 個別 */
  renewalTermMonths: number | null;
  /** 価格（税抜）。0 = 無料 / null = 未設定・別請求 */
  priceExclTax: number | null;
  autoRenew: boolean;
  startDateRule: APICompsSchema["StartDateRuleEnum"];
  /** 運営が契約登録で選べるか */
  selectableInAdmin: boolean;
  /**
   * 公開申込フォーム（NoAuth）で顧客に選ばせるか。
   * 値はデモ実装（demo/ContractList/src/app/data/planMaster.ts）の
   * selectableInPublicForm をそのまま移したもの。トライアル・個別契約・
   * 代理店専用プランは顧客に出さない。
   */
  selectableInPublicForm: boolean;
};

/** カスタム（自由記述）を表すキー。API へは planKey として送らない。 */
export const CUSTOM_PLAN_KEY = "custom";

export const CONTRACT_PLAN_MASTER: ContractPlanMaster[] = [
  {
    key: "store_base",
    dashboardLabel: "ストア",
    category: "store",
    initialTermMonths: 6,
    renewalTermMonths: 6,
    priceExclTax: 30000,
    autoRenew: true,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: false,
  },
  {
    key: "corp_base",
    dashboardLabel: "コーポレート",
    category: "corporate",
    initialTermMonths: 6,
    renewalTermMonths: 6,
    priceExclTax: 100000,
    autoRenew: true,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: false,
  },
  {
    key: "store_light",
    dashboardLabel: "ストア　ライト",
    category: "store",
    initialTermMonths: 6,
    renewalTermMonths: 6,
    priceExclTax: 30000,
    autoRenew: true,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: true,
  },
  {
    key: "store_standard",
    dashboardLabel: "ストア　スタンダード",
    category: "store",
    initialTermMonths: 12,
    renewalTermMonths: 12,
    priceExclTax: 50000,
    autoRenew: true,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: true,
  },
  {
    key: "store_pro",
    dashboardLabel: "ストア　プロ",
    category: "store",
    initialTermMonths: 12,
    renewalTermMonths: 12,
    priceExclTax: 80000,
    autoRenew: true,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: true,
  },
  {
    key: "corp_light",
    dashboardLabel: "コーポレート　ライト",
    category: "corporate",
    initialTermMonths: 6,
    renewalTermMonths: 6,
    priceExclTax: 100000,
    autoRenew: true,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: true,
  },
  {
    key: "corp_standard",
    dashboardLabel: "コーポレート　スタンダード",
    category: "corporate",
    initialTermMonths: 12,
    renewalTermMonths: 12,
    priceExclTax: 150000,
    autoRenew: true,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: true,
  },
  {
    key: "corp_pro",
    dashboardLabel: "コーポレート　プロ",
    category: "corporate",
    initialTermMonths: 12,
    renewalTermMonths: 12,
    priceExclTax: 200000,
    autoRenew: true,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: true,
  },
  {
    key: "trial_store",
    dashboardLabel: "トライアル　ストア",
    category: "store",
    initialTermMonths: 3,
    renewalTermMonths: 6,
    priceExclTax: 30000,
    autoRenew: true,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: false,
  },
  {
    key: "trial_corp",
    dashboardLabel: "トライアル　コーポレート",
    category: "corporate",
    initialTermMonths: 3,
    renewalTermMonths: 6,
    priceExclTax: 100000,
    autoRenew: true,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: false,
  },
  {
    key: "store_standard_trial",
    dashboardLabel: "ストア　スタンダード　トライアル",
    category: "store",
    initialTermMonths: 6,
    renewalTermMonths: 12,
    priceExclTax: 50000,
    autoRenew: true,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: false,
  },
  {
    key: "store_pro_trial",
    dashboardLabel: "ストア　プロ　トライアル",
    category: "store",
    initialTermMonths: 6,
    renewalTermMonths: 12,
    priceExclTax: 80000,
    autoRenew: true,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: false,
  },
  {
    key: "corp_standard_trial",
    dashboardLabel: "コーポレート　スタンダード　トライアル",
    category: "corporate",
    initialTermMonths: 6,
    renewalTermMonths: 12,
    priceExclTax: 150000,
    autoRenew: true,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: false,
  },
  {
    key: "corp_pro_trial",
    dashboardLabel: "コーポレート　プロ　トライアル",
    category: "corporate",
    initialTermMonths: 6,
    renewalTermMonths: 12,
    priceExclTax: 200000,
    autoRenew: true,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: false,
  },
  {
    key: "gnavi_store_light",
    dashboardLabel: "ぐるなびストア　ライト",
    category: "special",
    initialTermMonths: 1,
    renewalTermMonths: 1,
    priceExclTax: null,
    autoRenew: true,
    startDateRule: "first_day_of_month",
    selectableInAdmin: true,
    selectableInPublicForm: false,
  },
  {
    key: "gnavi_corp_light",
    dashboardLabel: "ぐるなびコーポレート　ライト",
    category: "special",
    initialTermMonths: 1,
    renewalTermMonths: 1,
    priceExclTax: null,
    autoRenew: true,
    startDateRule: "first_day_of_month",
    selectableInAdmin: true,
    selectableInPublicForm: false,
  },
  {
    key: "spot3m_store_light",
    dashboardLabel: "3ヶ月スポット　ストア　ライト",
    category: "special",
    initialTermMonths: 3,
    renewalTermMonths: null,
    priceExclTax: 45000,
    autoRenew: false,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: true,
  },
  {
    key: "spot3m_store_standard",
    dashboardLabel: "3ヶ月スポット　ストア　スタンダード",
    category: "special",
    initialTermMonths: 3,
    renewalTermMonths: null,
    priceExclTax: 75000,
    autoRenew: false,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: true,
  },
  {
    key: "spot3m_store_pro",
    dashboardLabel: "3ヶ月スポット　ストア　プロ",
    category: "special",
    initialTermMonths: 3,
    renewalTermMonths: null,
    priceExclTax: 120000,
    autoRenew: false,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: true,
  },
  {
    key: "spot3m_corp_light",
    dashboardLabel: "3ヶ月スポット　コーポレート　ライト",
    category: "special",
    initialTermMonths: 3,
    renewalTermMonths: null,
    priceExclTax: 150000,
    autoRenew: false,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: true,
  },
  {
    key: "spot3m_corp_standard",
    dashboardLabel: "3ヶ月スポット　コーポレート　スタンダード",
    category: "special",
    initialTermMonths: 3,
    renewalTermMonths: null,
    priceExclTax: 225000,
    autoRenew: false,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: true,
  },
  {
    key: "spot3m_corp_pro",
    dashboardLabel: "3ヶ月スポット　コーポレート　プロ",
    category: "special",
    initialTermMonths: 3,
    renewalTermMonths: null,
    priceExclTax: 300000,
    autoRenew: false,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: true,
  },
  {
    key: "partner_store",
    dashboardLabel: "パートナー店舗",
    category: "special",
    initialTermMonths: 3,
    renewalTermMonths: null,
    priceExclTax: 0,
    autoRenew: false,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: false,
  },
  {
    key: "suntory_v1",
    dashboardLabel: "サントリー特別 Ver.1",
    category: "special",
    initialTermMonths: 6,
    renewalTermMonths: null,
    priceExclTax: 250000,
    autoRenew: false,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: false,
  },
  {
    key: "suntory_v2",
    dashboardLabel: "サントリー特別 Ver.2",
    category: "special",
    initialTermMonths: 3,
    renewalTermMonths: null,
    priceExclTax: 100000,
    autoRenew: false,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: false,
  },
  {
    key: "custom",
    dashboardLabel: "カスタム",
    category: "special",
    initialTermMonths: null,
    renewalTermMonths: null,
    priceExclTax: null,
    autoRenew: false,
    startDateRule: "normal",
    selectableInAdmin: true,
    selectableInPublicForm: true,
  },
];

const BY_KEY = new Map(CONTRACT_PLAN_MASTER.map((plan) => [plan.key, plan]));

export const findContractPlan = (key: string | null | undefined): ContractPlanMaster | undefined =>
  key ? BY_KEY.get(key) : undefined;

/** 運営が契約登録で選べるプラン（表示順はマスタの並び）。 */
export const selectableContractPlans = (): ContractPlanMaster[] =>
  CONTRACT_PLAN_MASTER.filter((plan) => plan.selectableInAdmin);

/** カスタム（自由記述）か。true のとき API へ planKey を送らず planFreeText を必須にする。 */
export const isCustomPlan = (key: string | null | undefined): boolean => key === CUSTOM_PLAN_KEY;

/**
 * 契約終了日の初期値を出す。契約開始日の n ヶ月後の応当日の前日。
 *
 * 例: 2026-10-01 開始・6ヶ月 → 2027-03-31。
 * 応当日が存在しない月末開始（例 2026-08-31 の6ヶ月後＝2027-02-31）は、
 * 民法143条2項と同じくその月の末日を終了日とする（2027-02-28）。
 * JS の月加算は繰り上がる（2027-03-03）ので、そのまま −1日 すると 2027-03-02 になってしまう。
 * 開始日ルール（月初固定など）は MVP では参考値で、運営が手入力する前提なのでここでは見ない。
 * マスタに初回契約期間が無い（カスタム）場合は null を返す。
 */
export const deriveContractEndDate = (
  contractStartDate: string | null | undefined,
  planKey: string | null | undefined,
): string | null => {
  if (!contractStartDate) return null;
  const plan = findContractPlan(planKey);
  if (plan?.initialTermMonths == null) return null;

  const [y, m, d] = contractStartDate.split("-").map(Number);
  if (!y || !m || !d) return null;

  // UTC で計算して日付だけ取り出す（ローカルタイムゾーンで1日ずれるのを避ける）。
  const targetMonth = m - 1 + plan.initialTermMonths;
  // 「翌月の0日」＝対象月の末日。応当日がこの日数を超えるなら、その月に応当日は無い。
  const lastDayOfTargetMonth = new Date(Date.UTC(y, targetMonth + 1, 0)).getUTCDate();
  const end =
    d > lastDayOfTargetMonth
      ? new Date(Date.UTC(y, targetMonth, lastDayOfTargetMonth))
      : new Date(Date.UTC(y, targetMonth, d - 1));
  return end.toISOString().slice(0, 10);
};

/**
 * 保存済みの契約を、フォームが扱うプランキーへ読み替える。
 *
 * カスタム契約は API が `plan: null` ＋ `planFreeText` で返すので、そのままでは
 * フォームの `CUSTOM_PLAN_KEY` と比べられない。プランも自由記述も無い契約は "" になる。
 */
export const savedPlanKeyOf = (saved: { planKey?: string | null; planFreeText?: string | null }): string =>
  saved.planKey || (saved.planFreeText ? CUSTOM_PLAN_KEY : "");

/**
 * プランを「編集した」か。サーバの isPlanEdit と同じで、キーの有無ではなく**値の比較**で見る。
 * フォーム全体を送る作りなので、キーの有無で判定すると特記だけ直しても編集扱いになってしまう。
 *
 * **自由記述だけの変更でも true になる。** 枠・ペイメントリンクの上書き解除はキーが変わった
 * ときだけなので、そちらの判定にこれを使わないこと（サーバの isPlanEdit と同じ注意）。
 */
export const isPlanChanged = (
  input: { planKey: string; planFreeText: string },
  saved: { planKey?: string | null; planFreeText?: string | null },
): boolean => {
  const savedFreeText = saved.planFreeText || "";
  return input.planKey !== savedPlanKeyOf(saved) || input.planFreeText.trim() !== savedFreeText;
};

/**
 * プラン入力の必須チェック。問題なければ null。
 *
 * サーバは新規作成では必ず resolvePlan を通すので必須、編集ではプランを変えたときだけ検証する。
 * ここを無条件に必須にすると、プランが入っていない既存契約は
 * ストア割当や特記の修正すら保存できなくなる。
 */
export const validatePlanInput = (
  input: { planKey: string; planFreeText: string },
  saved: { planKey?: string | null; planFreeText?: string | null } | null,
): string | null => {
  const isEdit = saved !== null;
  if (isEdit && !isPlanChanged(input, saved)) return null;
  if (!input.planKey) return "プランを選択してください";
  if (isCustomPlan(input.planKey) && !input.planFreeText.trim()) {
    return "カスタム／その他のプラン内容を入力してください";
  }
  return null;
};

/**
 * 公開申込フォームで選ばせるプラン。
 * `custom`（カスタム／その他）も含むので、呼び出し側は isCustomPlan で分岐して
 * planKey を送らず planFreeText を必須にすること。
 */
export const publicFormPlans = (): ContractPlanMaster[] =>
  CONTRACT_PLAN_MASTER.filter((p) => p.selectableInPublicForm);
